import { el, awaitRedraw, inject, create } from "@dhx/ts-common/dom";
import { View } from "@dhx/ts-common/view";
import { IContainerHandlersMap, ItemEvent, IContainerConfig, IContainer, IBaseLayoutItem } from "../types";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { isEmptyObj } from "@dhx/ts-common/core";
import { Layout } from "@dhx/ts-layout";
import { baseProps } from "../helper";

export class Container extends View implements IContainer {
	public config: IContainerConfig;
	public container: Layout;
	public events: IEventSystem<ItemEvent, IContainerHandlersMap> = new EventSystem();

	constructor(container, config: IContainerConfig) {
		super(container, {
			disabled: false,
			hidden: false,
			width: "content",
			height: "content",
			...config,
		});

		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.container) {
			this.container.destructor();
		}

		const cellConfig: any = {};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name" && key !== "width" && key !== "height") {
				this.config[key] = config[key];
				if (key !== "validation") {
					cellConfig[key] = config[key];
				}
			}
		}

		this.container = new Layout(null, {
			rows: [cellConfig],
		});

		cellConfig.html && this.container.attachHTML(cellConfig.html);

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
		this.paint();

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	attach(widget: any) {
		this.container.attach(widget);
	}
	attachHTML(html: string) {
		this.container.attachHTML(html);
	}
	show(): void {
		const { hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [undefined])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [undefined]);
	}
	hide(init?: boolean): void {
		const { hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [undefined, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [undefined, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	setProperties(propertyConfig: IBaseLayoutItem): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (baseProps.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): IBaseLayoutItem {
		const obj: IBaseLayoutItem = {};
		for (const prop of baseProps) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	protected _getRootView(): any {
		return this.container.getRootView();
	}
	protected _draw() {
		const { name, id, disabled } = this.config;
		const css = disabled ? " dhx_form-group--disabled" : "";
		return el(".dhx_form-group.dhx_form-group--container", { class: css, "data-dhx-id": name || id }, [
			inject(this._getRootView()),
		]);
	}
}
